import { useEffect, useMemo, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Badge, Button, Input, Modal, Select, Textarea, useToast } from '@/components/ui'
import {
  fileToBase64,
  useCreateContractTemplate,
  useReplaceContractTemplate,
  useUpdateContractTemplate,
} from '../api/templatesApi.ts'
import { useProducts } from '../api/contractsApi.ts'
import type { ContractTemplate } from '../types.ts'

interface TemplateUploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: ContractTemplate | null
  onSaved?: () => void
}

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

export function TemplateUploadModal({ open, onOpenChange, template, onSaved }: TemplateUploadModalProps) {
  const isEdit = Boolean(template)
  const { toast } = useToast()
  const create = useCreateContractTemplate()
  const update = useUpdateContractTemplate(template?.id ?? '')
  const replace = useReplaceContractTemplate(template?.id ?? '')
  const { data: products = [] } = useProducts({ active: true })

  const fileInput = useRef<HTMLInputElement | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [productId, setProductId] = useState('')
  const [pendingFile, setPendingFile] = useState<{ name: string; base64: string; size: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setPendingFile(null)
    if (template) {
      setName(template.name)
      setDescription(template.description ?? '')
      setProductId(template.productId ?? '')
    } else {
      setName('')
      setDescription('')
      setProductId('')
    }
  }, [open, template])

  const productOptions = useMemo(
    () => [
      { value: '', label: '— Optional, kein bestimmtes Produkt —' },
      ...products.map((p) => ({ value: p.id, label: p.name })),
    ],
    [products],
  )

  async function handleFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.docx') && file.type !== DOCX_MIME) {
      setError('Bitte eine DOCX-Datei wählen (kein PDF, kein DOC).')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('Datei zu groß (max 15 MB).')
      return
    }
    try {
      const base64 = await fileToBase64(file)
      setPendingFile({ name: file.name, base64, size: file.size })
      setError(null)
    } catch (err) {
      setError(`Datei konnte nicht gelesen werden: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  function handleSave() {
    if (!name.trim()) {
      setError('Name ist Pflicht.')
      return
    }
    if (!isEdit && !pendingFile) {
      setError('Bitte eine DOCX-Datei wählen.')
      return
    }

    const onError = (err: Error) => setError(err.message || 'Speichern fehlgeschlagen.')

    if (isEdit && template) {
      // Metadaten + ggf. Datei ersetzen
      update.mutate(
        { name: name.trim(), description: description || null, productId: productId || null },
        {
          onSuccess: () => {
            if (pendingFile) {
              replace.mutate(
                { fileContentBase64: pendingFile.base64, originalFileName: pendingFile.name },
                {
                  onSuccess: () => {
                    toast({ title: 'Vorlage aktualisiert', tone: 'success' })
                    onSaved?.()
                  },
                  onError,
                },
              )
            } else {
              toast({ title: 'Vorlage aktualisiert', tone: 'success' })
              onSaved?.()
            }
          },
          onError,
        },
      )
    } else {
      create.mutate(
        {
          name: name.trim(),
          description: description || null,
          productId: productId || null,
          fileContentBase64: pendingFile!.base64,
          originalFileName: pendingFile!.name,
        },
        {
          onSuccess: () => {
            toast({ title: 'Vorlage angelegt', tone: 'success' })
            onSaved?.()
          },
          onError,
        },
      )
    }
  }

  const isPending = create.isPending || update.isPending || replace.isPending

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={isEdit ? 'Vorlage bearbeiten' : 'Neue Vertragsvorlage'}
      description="DOCX-Datei mit Platzhaltern wie {vertragsnummer}, {kunde_name}, {beginn}, {positionen} hochladen. Verfügbare Felder: vertragsnummer, vertragsname, vertragsstatus, beginn, ende, laufzeit, abrechnung, kuendigung_tage, auto_verlaengerung, notizen, waehrung, produkt, tarif, kunde_name, kunde_sap, kunde_tanss, kunde_strasse, kunde_plz, kunde_ort, kunde_land, heute, plus #positionen-Schleife mit nummer/bezeichnung/soll_menge/einheit/sap_artikel."
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}

        <Input
          label="Name"
          hint="z. B. „Managed Workplace v2.3"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />

        <Textarea
          label="Beschreibung"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        <Select
          label="Standard-Produkt"
          hint="Optional — bei Vertragsanlage wird die Vorlage dann vorgeschlagen"
          value={productId}
          onValueChange={setProductId}
          options={productOptions}
        />

        <div className="space-y-2">
          <p className="text-small font-medium text-gray-700">DOCX-Datei</p>
          <div className="flex items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => {
                const file = e.currentTarget.files?.[0]
                if (file) void handleFile(file)
                e.currentTarget.value = ''
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Upload className="w-4 h-4" />}
              onClick={() => fileInput.current?.click()}
            >
              Datei wählen
            </Button>
            {pendingFile ? (
              <Badge variant="brand" size="sm">
                {pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)} KB
              </Badge>
            ) : isEdit && template?.originalFileName ? (
              <Badge variant="neutral" size="sm">
                aktuell: {template.originalFileName} · v{template.version}
              </Badge>
            ) : (
              <span className="text-caption text-gray-500">keine Datei gewählt</span>
            )}
          </div>
          <p className="text-caption text-gray-500">
            Max 15 MB. Erkennbare Platzhalter werden nach dem Anlegen unter den Vorlagen angezeigt.
          </p>
        </div>
      </div>
    </Modal>
  )
}
